import * as constants from "../../constants";
import selectAccountFlagsContainer, { canEditReason } from "../selectors/accountFlags";
import fetch from "../util/api-ajax";
import { updateCandidateState } from "./dnp";

const createErrorMessage = (message) => ({
    type: constants.ACCOUNT_FLAG_SET_ERROR,
    message,
});

const createSuccessMessage = (message) => ({
    type: constants.ACCOUNT_FLAG_SET_SUCCESS,
    message,
});

export const setErrorMessage = (message) => (dispatch) => {
    dispatch(createErrorMessage(message));
};

export const setSuccessMessage = (message) => (dispatch) => {
    dispatch(createSuccessMessage(message));
};

// genericFetch rejects with the parsed JSON error body, not an Error, so
// message may be missing, or - for a structured error body - an object,
// which would crash the Snackbar's render if passed through as-is.
const getErrorMessage = (e, fallback = "Failed to update flags") => {
    const m = e?.message || e?.error;
    return typeof m === "string" && m ? m : fallback;
};

const setLoading = (isLoading) => ({
    type: constants.ACCOUNT_FLAG_SET_LOADING,
    isLoading,
});

export const fetchAccountFlagTypes = () => (dispatch) => {
    return fetch(`${constants.ACCOUNT_FLAG_FETCH_URL}/`)
        .then((accountFlagTypes) => {
            dispatch({
                type: constants.ACCOUNT_FLAG_TYPES_LOAD,
                accountFlagTypes: accountFlagTypes.Items,
            });
        })
        .catch((err) => {
            dispatch(setErrorMessage(getErrorMessage(err, "Failed to load flag types")));
        });
};

export const fetchAccountFlags = () => (dispatch, getState) => {
    const { selectedAccountID } = selectAccountFlagsContainer()(getState());
    if (!selectedAccountID) {
        return Promise.resolve();
    }
    dispatch(setLoading(true));
    return fetch(
        `${constants.ACCOUNT_FLAGS_MAIN_URL}/${encodeURIComponent(selectedAccountID)}/accountflags`,
        {
            is_expired: false,
            filter_by_expired: true,
        }
    )
        .then((accountFlags) => {
            dispatch({
                type: constants.ACCOUNT_FLAGS_LOAD,
                selectedAccountID,
                accountFlags: accountFlags.Items,
            });
            dispatch(setLoading(false));
        })
        .catch((err) => {
            dispatch(setErrorMessage(getErrorMessage(err, "Failed to load flags")));
            dispatch(setLoading(false));
        });
};

const setDnpQueFlag = (dispatch, getState) => {
    const {
        selectedAccountID,
        selectedAccountFlags,
    } = selectAccountFlagsContainer()(getState());
    const dnpQueueIndex = getState().dnp.accounts.findIndex(
        (a) => a.accountID === selectedAccountID
    );
    updateCandidateState(dnpQueueIndex, {
        hasDnpFlag: Boolean(selectedAccountFlags.length),
    })(dispatch);
};

export const cancelChanges = () => (dispatch, getState) => {
    const { selectedAccountID } = selectAccountFlagsContainer()(getState());
    if (!selectedAccountID) {
        return Promise.resolve();
    }
    dispatch({
        type: constants.ACCOUNT_FLAG_CANCEL_CHANGES,
        selectedAccountID,
    });
    return dispatch(fetchAccountFlags());
};

export const sendAccountsFlagRequest = () => (dispatch, getState) => {
    const {
        selectedAccountID,
        selectedAccountFlags,
        adminUser,
    } = selectAccountFlagsContainer()(getState());
    // af.reason may hold a previously-stored value the user never touched -
    // only include it if they're actually allowed to write it.
    const userCanEditReason = canEditReason(adminUser);
    const accountFlagsToAdd = selectedAccountFlags.filter(
        (af) => af.action === "add"
    );
    const accountFlagsToRemove = selectedAccountFlags.filter(
        (af) => af.action === "remove"
    );

    dispatch(setLoading(true));

    const promises = [
        ...accountFlagsToAdd.map((af) =>
            fetch(
                `${constants.ACCOUNT_FLAGS_MAIN_URL}/${encodeURIComponent(selectedAccountID)}/accountflags/create`,
                {
                    account_flag_type_id: af.flag_type,
                    expiration_date: af.expired,
                    // reason null = untouched this session, omit both keys so the
                    // server leaves the stored value alone; "" = explicitly cleared.
                    ...(userCanEditReason && af.reason != null ? { reason: af.reason, reason_is_set: true } : {}),
                }
            ).then((accountFlag) => {
                dispatch({
                    type: constants.ACCOUNT_FLAGS_ADD,
                    selectedAccountID,
                    accountFlag,
                });
                return setDnpQueFlag(dispatch, getState);
            })
        ),
        ...accountFlagsToRemove.filter(af => af.account_flag_id !== undefined).map((af) =>
            fetch(
                `${constants.ACCOUNT_FLAGS_MAIN_URL}/${encodeURIComponent(selectedAccountID)}/accountflags/${encodeURIComponent(af.account_flag_id)}/delete`,
                { account_flag_type_id: af.flag_type }
            ).then((accountFlag) => {
                dispatch({
                    type: constants.ACCOUNT_FLAGS_DELETE,
                    selectedAccountID,
                    accountFlag,
                });
                return setDnpQueFlag(dispatch, getState);
            })
        ),
    ];

    return Promise.all(promises)
        .then(() => {
            dispatch(setSuccessMessage("Flag has been updated!"))
            dispatch(fetchAccountFlagTypes())
        })
        .catch((e) => dispatch(setErrorMessage(getErrorMessage(e))))
        .finally(() => {
            // cancelChanges -> fetchAccountFlags owns isLoading for the
            // reload; only reset it here if the account's since been
            // deselected, since fetchAccountFlags then early-returns without
            // touching it (leaving the spinner stuck on otherwise).
            const { selectedAccountID: currentAccountID } = selectAccountFlagsContainer()(getState());
            if (!currentAccountID) {
                dispatch(setLoading(false));
            }
            return cancelChanges()(dispatch, getState);
        });
};

export const toggleAccountFlag = (flagId, expired, userAction, reason) => (dispatch, getState) => {
    const { selectedAccountID } = selectAccountFlagsContainer()(getState());
    return dispatch({
        type: constants.ACCOUNT_FLAG_TOGGLE,
        flagId,
        selectedAccountID,
        expired: expired,
        userAction,
        reason,
    });
};

export const closeSnackbar = () => (dispatch) => {
    return dispatch({ type: constants.ACCOUNT_FLAG_DISMISS_MESSAGE });
};

export const toggleCategoryView = (category) => (dispatch) => {
    return dispatch({
        type: constants.ACCOUNT_FLAG_TOGGLE_CATEGORY_VIEW,
        category,
    });
};
